<script setup lang="ts">
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faClock, faUserLock, faSignOutAlt, faShieldAlt } from '@fortawesome/free-solid-svg-icons'

library.add(faClock, faUserLock, faSignOutAlt, faShieldAlt)

const props = defineProps<{
  show: boolean
  remainingSeconds: number
}>()

const emit = defineEmits(['stay', 'logout'])

const formattedTime = computed(() => {
  const mins = Math.floor(props.remainingSeconds / 60)
  const secs = props.remainingSeconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
})

const progress = computed(() => {
  // Assuming 60s warning by default
  const total = 60 
  return Math.min(100, (props.remainingSeconds / total) * 100)
})
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md">
      <div class="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 transform transition-all">
        <!-- Header with animated background -->
        <div class="bg-gradient-to-br from-amber-500 to-orange-600 p-8 text-white relative">
          <div class="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          
          <div class="relative flex flex-col items-center text-center">
            <div class="bg-white/20 p-4 rounded-2xl backdrop-blur-sm mb-4 border border-white/30">
              <font-awesome-icon icon="user-lock" class="text-3xl" />
            </div>
            <h3 class="text-2xl font-black tracking-tight uppercase">Session Security</h3>
            <p class="text-amber-50 font-medium text-sm mt-1 opacity-90">Inactivity timeout approaching</p>
          </div>
        </div>

        <!-- Body -->
        <div class="p-8 text-center">
          <div class="relative inline-flex items-center justify-center mb-8">
            <!-- Simple Countdown Circle -->
            <div class="text-5xl font-black text-gray-900 tracking-tighter tabular-nums">
              {{ formattedTime }}
            </div>
          </div>
          
          <p class="text-gray-600 font-medium px-4 leading-relaxed">
            Your session is about to expire for security reasons. <br/>
            <span class="text-gray-900 font-bold">Would you like to stay logged in?</span>
          </p>
        </div>

        <!-- Actions -->
        <div class="p-8 pt-0 space-y-3">
          <button
            @click="emit('stay')"
            class="w-full py-4 bg-gray-900 hover:bg-black text-white rounded-2xl font-black text-lg transition-all transform active:scale-[0.98] shadow-xl shadow-gray-200 flex items-center justify-center gap-3"
          >
            <font-awesome-icon icon="shield-alt" class="text-green-400" />
            STAY LOGGED IN
          </button>
          
          <button
            @click="emit('logout')"
            class="w-full py-4 bg-white hover:bg-gray-50 text-gray-500 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 border-2 border-transparent hover:border-gray-100"
          >
            <font-awesome-icon icon="sign-out-alt" />
            Logout Now
          </button>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-8 py-4 border-t border-gray-100 flex items-center justify-center gap-2">
           <div class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>
           <span class="text-[10px] font-black uppercase tracking-widest text-gray-400">Security Auto-Lock Active</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.modal-enter-to,
.modal-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>
